import * as THREE from 'three';
import { TextureGenerator } from './textures.js';
import { SectorGenerator } from './generator.js';
import { PlayerController } from './player.js';
import { multiplayer } from './multiplayer/MultiplayerManager.js';

export class WorldManager {
  constructor(containerId, audioSynth, startZoneType = 0) {
    this.container = document.getElementById(containerId);
    this.audio = audioSynth;
    this.startZoneType = startZoneType;

    // Core variables
    this.renderer = null;
    this.scene = null;
    this.camera = null;
    this.clock = new THREE.Clock();
    
    // Core game components
    this.textures = null;
    this.generator = null;
    this.player = null;

    // Particles (dust motes)
    this.dustGeometry = null;
    this.dustParticles = null;
    this.dustCount = 350;
    this.dustBounds = 18; // bounds box size around camera

    // Settings config
    this.settings = {
      fov: 75,
      bobbing: 0.4,
      flicker: true
    };

    // Tracking player grid coords to minimize generator cycles
    this.currentGx = 9999;
    this.currentGz = 9999;

    this.init();
  }

  /**
   * Initializes the Three.js rendering pipeline, scene, and camera.
   */
  init() {
    // 1. Scene setup with deep dark grey ambient fog
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color('#0b0c10');
    // Muffled atmosphere: softer exponential fog to allow seeing down halls
    this.scene.fog = new THREE.FogExp2('#0b0c10', 0.025);

    // 2. Camera setup
    const aspect = window.innerWidth / window.innerHeight;
    this.camera = new THREE.PerspectiveCamera(this.settings.fov, aspect, 0.1, 75);
    this.scene.add(this.camera);

    // 3. WebGLRenderer with high-fidelity filmic profiles
    this.renderer = new THREE.WebGLRenderer({ antialias: true, powerPreference: 'high-performance' });
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.shadowMap.enabled = false; // Disabled to prevent WebGL max texture units crash and match N64 retro look
    
    // Tone mapping exposure settings to make emissives glow beautifully
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.25;

    this.container.appendChild(this.renderer.domElement);

    // 3.5. Vintage Color CRT 80s/90s Post-Processing Scene & Quad setup
    const dpr = Math.min(window.devicePixelRatio, 2);
    this.postScene = new THREE.Scene();
    this.postCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
    
    this.rt = new THREE.WebGLRenderTarget(window.innerWidth * dpr, window.innerHeight * dpr, {
      minFilter: THREE.LinearFilter,
      magFilter: THREE.LinearFilter,
      format: THREE.RGBAFormat
    });

    this.crtShader = {
      uniforms: {
        tDiffuse: { value: null },
        uTime: { value: 0 },
        uResolution: { value: new THREE.Vector2(window.innerWidth * dpr, window.innerHeight * dpr) }
      },
      vertexShader: `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform sampler2D tDiffuse;
        uniform float uTime;
        uniform vec2 uResolution;
        varying vec2 vUv;

        // Curved CRT glass faceplate projection with micro-zoom
        vec2 curve(vec2 uv) {
          uv = uv * 2.0 - 1.0;
          vec2 offset = abs(uv.yx) / vec2(15.0, 13.0); // Extremely subtle, gentle lens curvature
          uv = uv + uv * offset * offset;
          uv = uv * 0.5 + 0.5;
          // Zoom from center by 1.015x to guarantee absolutely zero black corners or borders
          uv = (uv - 0.5) * 0.985 + 0.5;
          return clamp(uv, 0.0, 1.0);
        }

        // Faint analog noise static grain
        float noise(vec2 uv) {
          return fract(sin(dot(uv, vec2(12.9898, 78.233) + uTime)) * 43758.5453);
        }

        void main() {
          // Curved projection coordinates
          vec2 uv = curve(vUv);

          // Extremely subtle horizontal hum jitter (60Hz power sync oscillation)
          float humJitter = sin(uTime * 60.0) * 0.00007;
          uv.x += humJitter;

          // Dynamic rare glitch tracker (occurs briefly for 0.08s every 25 seconds to prevent a "broken" look)
          float timeVal = uTime;
          float glitchCycle = floor(timeVal / 25.0);
          float glitchSeed = fract(sin(glitchCycle * 43758.5453));
          
          float glitchTime = mod(timeVal, 25.0);
          float glitchActive = step(glitchSeed, 0.40) * step(glitchTime, 0.12);

          // Very faint horizontal sync line displacements during glitch rolls
          float lineJitter = 0.0;
          if (glitchActive > 0.0) {
            float wave = sin(uv.y * 30.0 + uTime * 8.0);
            lineJitter = wave * 0.0006 * glitchActive;
          }

          // Minimal Chromatic Aberration RGB splitting
          float rgbShift = 0.0004 + 0.0006 * glitchActive;
          vec2 uvR = vec2(uv.x + rgbShift + lineJitter, uv.y);
          vec2 uvG = vec2(uv.x + lineJitter, uv.y);
          vec2 uvB = vec2(uv.x - rgbShift + lineJitter, uv.y);

          float r = texture2D(tDiffuse, uvR).r;
          float g = texture2D(tDiffuse, uvG).g;
          float b = texture2D(tDiffuse, uvB).b;

          // Occasional faint signal reflections (ghosting) during glitches
          if (glitchActive > 0.0) {
            r += texture2D(tDiffuse, uvR - vec2(0.002, 0.0)).r * 0.06;
            b += texture2D(tDiffuse, uvB + vec2(0.002, 0.0)).b * 0.06;
          }

          vec3 col = vec3(r, g, b);

          // Microscopic RGB Phosphor stripes (Shadow Mask simulation)
          float xCoord = uv.x * uResolution.x;
          float stripePattern = mod(floor(xCoord), 3.0);
          vec3 shadowMask = vec3(1.0);
          if (stripePattern < 1.0) {
            shadowMask = vec3(1.015, 0.99, 0.99); // Red bias
          } else if (stripePattern < 2.0) {
            shadowMask = vec3(0.99, 1.015, 0.99); // Green bias
          } else {
            shadowMask = vec3(0.99, 0.99, 1.015); // Blue bias
          }
          
          // Faint horizontal stripe matrix grid structure (avoids moiré patterns)
          float yCoord = uv.y * uResolution.y;
          if (mod(floor(yCoord * 0.95), 3.0) < 1.0) {
            shadowMask *= 0.985;
          }
          col *= shadowMask;

          // Very fine, thin horizontal scanlines (no blurring, clean overlay feel)
          float scanline = sin(uv.y * uResolution.y * 1.5) * 0.018;
          col -= scanline;

          // Subtle brightness breathing representing vintage current fluctuations
          float brightnessBreath = 1.0 + sin(uTime * 2.5) * 0.0015;
          col *= brightnessBreath;

          // Soft analog bleed representing retro phosphor color bleed (mix-based to preserve exact exposure)
          vec3 baseCol = col;
          vec3 bleed = texture2D(tDiffuse, uv + vec2(0.0006, 0.0)).rgb;
          bleed += texture2D(tDiffuse, uv - vec2(0.0006, 0.0)).rgb;
          bleed *= 0.5;
          col = mix(baseCol, bleed, 0.035);

          // Moderate analog bloom of bright highlights using a non-additive mix
          vec3 bloomSample = texture2D(tDiffuse, uv + vec2(0.0012, 0.0006)).rgb;
          bloomSample += texture2D(tDiffuse, uv + vec2(-0.0012, -0.0006)).rgb;
          bloomSample *= 0.5;
          col = mix(col, bloomSample, 0.015);

          // Cathode-tube phosphor base glow (prevents flat dead LCD blacks, adding warm tube light emission)
          vec3 phosphorTubeGlow = vec3(0.016, 0.015, 0.012);
          col += phosphorTubeGlow;

          // Light drive boost mimicking cathode-ray beam drive phosphor excitation (increases overall illumination)
          col *= 1.08;

          // Cathode ray border vignette (soft corner edge dropoff)
          float vign = uv.x * uv.y * (1.0 - uv.x) * (1.0 - uv.y);
          vign = clamp(pow(16.0 * vign, 0.08), 0.0, 1.0);
          col *= vign;

          // Barely perceptual static/noise grain
          float grainAmt = 0.004 + 0.004 * glitchActive;
          float grain = (noise(uv) - 0.5) * grainAmt;
          col += vec3(grain);

          // Saturated phosphor color profiling (subtle shift to maintain crisp room lighting)
          col.r *= 1.005;
          col.b *= 0.995;

          gl_FragColor = vec4(clamp(col, 0.0, 1.0), 1.0);
        }
      `
    };

    const postPlane = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), new THREE.ShaderMaterial(this.crtShader));
    this.postScene.add(postPlane);

    // 4. Ambient light to prevent pitch-black shadow surfaces
    const ambientLight = new THREE.AmbientLight(0xfff5e0, 0.7); // Bright base lighting
    this.scene.add(ambientLight);

    // Overhead bounce light representing fluorescent ceiling grid emission
    const ceilingBounceLight = new THREE.DirectionalLight(0xfff8e7, 0.6); // Strong even ceiling projection
    ceilingBounceLight.position.set(0, 10, 0); // pointing straight down
    this.scene.add(ceilingBounceLight);

    // 5. Initialize PBR Texture Generator and Infinite Sector Builder
    this.textures = new TextureGenerator(THREE);
    this.generator = new SectorGenerator(this.scene, this.textures, this.audio);

    // Find the closest sector index gx, gz matching this.startZoneType procedurally
    let startGx = 0;
    let startGz = 0;
    let found = false;
    for (let r = 0; r < 50; r++) {
      for (let dx = -r; dx <= r; dx++) {
        for (let dz = -r; dz <= r; dz++) {
          if (Math.abs(dx) === r || Math.abs(dz) === r) {
            const hash = Math.abs(this.generator.hash2D(dx, dz));
            if (hash % 10 === this.startZoneType) {
              // Ensure this starting sector has at least one open exit boundary to avoid entrapment
              const finalN = this.generator.isSolidBoundary(dx, dz, 'horizontal');
              const finalS = this.generator.isSolidBoundary(dx, dz + 1, 'horizontal');
              const finalE = this.generator.isSolidBoundary(dx + 1, dz, 'vertical');
              const finalW = this.generator.isSolidBoundary(dx, dz, 'vertical');
              const hasExit = !finalN || !finalS || !finalE || !finalW;
              if (hasExit) {
                startGx = dx;
                startGz = dz;
                found = true;
                break;
              }
            }
          }
        }
        if (found) break;
      }
      if (found) break;
    }

    // Set starting coordinates in generator for elevator and plant safety check
    this.generator.startGx = startGx;
    this.generator.startGz = startGz;

    // 6. Initialize Player movement controller
    this.player = new PlayerController(this.camera, this.renderer.domElement, this.audio);
    // Align starting camera position to the exact center of our chosen spawning sector
    const startX = startGx * this.generator.sectorSize;
    const startZ = startGz * this.generator.sectorSize;
    this.player.position.set(startX, 1.75, startZ);

    // 7. Generate subtle floating gold dust motes
    this.buildDustParticles();

    // 7.5. Initialize Spooky Distant Ghostly Shadows
    this.buildGhostShadow();

    // 8. Event listeners
    this.resizeHandler = this.onWindowResize.bind(this);
    window.addEventListener('resize', this.resizeHandler);

    // 9. Initial sector stream seeding
    this.updateSectorsStreaming(true);

    // 10. Start drawing loop
    this.animate();
  }

  /**
   * Spawns floating gold dust particle system.
   */
  buildDustParticles() {
    this.dustGeometry = new THREE.BufferGeometry();
    const positions = new Float32Array(this.dustCount * 3);

    // Scatter particles inside coordinates relative to start
    for (let i = 0; i < this.dustCount * 3; i += 3) {
      positions[i] = (Math.random() - 0.5) * this.dustBounds;
      positions[i + 1] = Math.random() * 4.5;
      positions[i + 2] = (Math.random() - 0.5) * this.dustBounds;
    }

    this.dustGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));

    // Custom material for tiny fuzzy dots
    const dustMat = new THREE.PointsMaterial({
      color: 0xe5c158,
      size: 0.04,
      transparent: true,
      opacity: 0.35,
      blending: THREE.AdditiveBlending
    });

    this.dustParticles = new THREE.Points(this.dustGeometry, dustMat);
    this.scene.add(this.dustParticles);
  }

  /**
   * Animates floating dust particles relative to the camera to maintain local density.
   */
  updateDustParticles(delta) {
    if (!this.dustParticles) return;

    const positions = this.dustGeometry.attributes.position.array;
    const px = this.camera.position.x;
    const py = this.camera.position.y;
    const pz = this.camera.position.z;

    const b = this.dustBounds;

    for (let i = 0; i < positions.length; i += 3) {
      // Apply subtle turbulent drift (slow offset drift)
      positions[i] += Math.sin(positions[i + 1] + i) * 0.05 * delta;
      positions[i + 1] -= 0.08 * delta; // slow downward fall
      positions[i + 2] += Math.cos(positions[i] + i) * 0.05 * delta;

      // Wrap-around checking relative to camera
      if (positions[i] < px - b/2) positions[i] = px + b/2;
      if (positions[i] > px + b/2) positions[i] = px - b/2;
      
      if (positions[i + 1] < 0) positions[i + 1] = 5.0; // reset to ceiling height

      if (positions[i + 2] < pz - b/2) positions[i + 2] = pz + b/2;
      if (positions[i + 2] > pz + b/2) positions[i + 2] = pz - b/2;
    }

    this.dustGeometry.attributes.position.needsUpdate = true;
  }

  /**
   * Tracks player absolute coordinate position, checks if they crossed sector boundary,
   * and triggers generation stream updates.
   */
  updateSectorsStreaming(force = false) {
    const px = this.player.camera.position.x;
    const pz = this.player.camera.position.z;

    // Calculate grid indices
    const gx = Math.floor((px + 10) / this.generator.sectorSize);
    const gz = Math.floor((pz + 10) / this.generator.sectorSize);

    // If grid sector changed or force loaded
    if (gx !== this.currentGx || gz !== this.currentGz || force) {
      this.currentGx = gx;
      this.currentGz = gz;

      // Trigger streaming radius 1 (generates a 3x3 grid centered around player - highly optimized!)
      const activeCollisionAABBs = this.generator.updateSectors(gx, gz, 1);

      // Pass active solids down to movement engine
      this.player.setCollisionBoxes(activeCollisionAABBs);

      // Trigger sector HUD update
      const hudSec = document.getElementById('hud-sector');
      if (hudSec) {
        // Format padded values: SEC [X: +002, Z: -005]
        const pad = (num) => {
          const sign = num >= 0 ? '+' : '-';
          const abs = Math.abs(num).toString().padStart(3, '0');
          return `${sign}${abs}`;
        };
        hudSec.textContent = `SEC [X: ${pad(gx)}, Z: ${pad(gz)}]`;
      }

      // Trigger room name HUD update
      const hashVal = Math.abs(this.generator.hash2D(gx, gz));
      const zoneType = hashVal % 10;

      let zoneName = "LOBBY INFINITO";
      if (zoneType === 1) zoneName = "SHOPPING SILENCIOSO";
      else if (zoneType === 2) zoneName = "HOTEL IMPOSIBLE";
      else if (zoneType === 3) zoneName = "AEROPUERTO DORMIDO";
      else if (zoneType === 4) zoneName = "CENTRO CORPORATIVO";
      else if (zoneType === 5) zoneName = "BIBLIOTECA INTERMINABLE";
      else if (zoneType === 6) zoneName = "CENTRO DE DATOS";
      else if (zoneType === 7) zoneName = "PISCINA SUBTERRÁNEA";
      else if (zoneType === 8) zoneName = "INVERNADERO VACÍO";
      else if (zoneType === 9) zoneName = "GALERÍA DE ARTE";

      const hudRoom = document.getElementById('hud-room-name');
      if (hudRoom) {
        hudRoom.textContent = window.translate ? window.translate(zoneName) : zoneName;
      }
    }
  }

  /**
   * Master requestAnimationFrame loop.
   */
  animate() {
    if (this.isDestroyed) return;
    requestAnimationFrame(this.animate.bind(this));

    // Cap delta at 0.1 to prevent giant physics jumps on tab blur
    const delta = Math.min(this.clock.getDelta(), 0.1);

    const isTouch = ('ontouchstart' in window) || (navigator.maxTouchPoints > 0);
    const pauseScreen = document.getElementById('pause-screen');
    const isPauseHidden = pauseScreen ? pauseScreen.classList.contains('hidden') : true;
    const isPlayActive = (document.pointerLockElement === this.renderer.domElement) || (isTouch && isPauseHidden);

    if (this.player && isPlayActive) {
      // 1. Update first-person movement & collision
      this.player.update(delta);

      // 2. Stream infinite sectors around new player position
      this.updateSectorsStreaming();

      // 3. Update dynamic lights (flickering bulbs) and elevators
      this.generator.updateEntities(delta, this.player.position, this.settings.flicker);

      // 4. Update golden dust particle vectors
      this.updateDustParticles(delta);

      // 4.5. Update distant ghostly shadows
      this.updateGhostShadow(delta);

      // 4.6. Update multiplayer network synchronizations
      if (multiplayer.config.enabled) {
        multiplayer.update(delta);
      }
    }

    // 5. Render active viewport scene using CRT Post-Processing pipeline
    if (this.renderer && this.scene && this.camera && this.rt) {
      if (this.crtShader) {
        this.crtShader.uniforms.uTime.value = this.clock.getElapsedTime();
        this.crtShader.uniforms.tDiffuse.value = this.rt.texture;
      }
      
      // Step A: Render standard liminal scene to offscreen Texture Target
      this.renderer.setRenderTarget(this.rt);
      this.renderer.render(this.scene, this.camera);
      
      // Step B: Apply curved phosphorus grid CRT filter & draw to screen canvas
      this.renderer.setRenderTarget(null);
      this.renderer.render(this.postScene, this.postCamera);
    }
  }

  /**
   * Handle dynamic viewport changes.
   */
  onWindowResize() {
    if (!this.camera || !this.renderer) return;

    const width = window.innerWidth;
    const height = window.innerHeight;
    const dpr = Math.min(window.devicePixelRatio, 2);

    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(width, height);

    if (this.rt) {
      this.rt.setSize(width * dpr, height * dpr);
    }
    if (this.crtShader && this.crtShader.uniforms.uResolution) {
      this.crtShader.uniforms.uResolution.value.set(width * dpr, height * dpr);
    }
  }

  /**
   * Modifies camera field of view at runtime.
   */
  setFOV(val) {
    this.settings.fov = val;
    if (this.camera) {
      this.camera.fov = val;
      this.camera.updateProjectionMatrix();
    }
  }

  /**
   * Modifies camera walk bobbing amplitude.
   */
  setBobbing(percent) {
    this.settings.bobbing = percent / 100;
    if (this.player) {
      this.player.bobIntensity = 0.08 * this.settings.bobbing; // scale down
    }
  }

  /**
   * Enables or disables atmospheric bulb flickering.
   */
  setFlickerActive(active) {
    this.settings.flicker = active;
  }

  /**
   * Spawns a creepy, distant vertical silhouette cylinder that fades in/out down corridors.
   */
  buildGhostShadow() {
    const ghostGeo = new THREE.CylinderGeometry(0.25, 0.25, 1.8, 8);
    const ghostMat = new THREE.MeshBasicMaterial({
      color: 0x010101, // Near pitch black
      transparent: true,
      opacity: 0.0
    });
    this.ghostMesh = new THREE.Mesh(ghostGeo, ghostMat);
    this.ghostMesh.position.set(0, -100, 0); // Hide initially below floor
    this.scene.add(this.ghostMesh);

    this.ghostActive = false;
    this.ghostTimer = 6.0; // Wait 6 seconds before first ghost roll
    this.ghostOpacity = 0.0;
  }

  /**
   * Updates dynamic ghostly shadows down long hallways.
   * Fades in slowly at 22-30m away, and vanishes instantly if approached under 11m.
   */
  updateGhostShadow(delta) {
    if (!this.ghostMesh) return;

    const px = this.camera.position.x;
    const pz = this.camera.position.z;

    if (!this.ghostActive) {
      this.ghostTimer -= delta;
      if (this.ghostTimer <= 0) {
        // Distant spawn along cardinal hallways
        const angle = Math.floor(Math.random() * 4) * (Math.PI / 2);
        const dist = 24 + Math.random() * 6; // 24m to 30m away in the fog!

        const sectorSize = this.generator.sectorSize;
        const currentSectorX = Math.floor((px + 10) / sectorSize) * sectorSize;
        const currentSectorZ = Math.floor((pz + 10) / sectorSize) * sectorSize;

        let gx = currentSectorX;
        let gz = currentSectorZ;

        // Position it straight down the vertical/horizontal long halls
        if (Math.abs(Math.cos(angle)) > 0.5) {
          gx += Math.sign(Math.cos(angle)) * dist;
        } else {
          gz += Math.sign(Math.sin(angle)) * dist;
        }

        this.ghostMesh.position.set(gx, 0.9, gz); // Eye level center
        this.ghostActive = true;
        this.ghostOpacity = 0.0;
        this.ghostMesh.material.opacity = 0.0;
        this.ghostTimer = 4.0 + Math.random() * 5.0; // stays visible for 4-9 seconds max
      }
    } else {
      // 1. Distance check
      const dx = px - this.ghostMesh.position.x;
      const dz = pz - this.ghostMesh.position.z;
      const distSq = dx * dx + dz * dz;

      // 2. Fade in slowly in fog
      this.ghostOpacity = Math.min(0.25, this.ghostOpacity + delta * 0.45);
      this.ghostMesh.material.opacity = this.ghostOpacity;

      // 3. Vanish instantly if player walks too close, or timer runs out
      this.ghostTimer -= delta;
      if (distSq < 144 || this.ghostTimer <= 0) { // inside 12 meters
        this.ghostOpacity = Math.max(0.0, this.ghostOpacity - delta * 1.8); // vanish fast!
        this.ghostMesh.material.opacity = this.ghostOpacity;

        if (this.ghostOpacity <= 0) {
          this.ghostActive = false;
          this.ghostMesh.position.set(0, -100, 0); // hide below floor
          this.ghostTimer = 15.0 + Math.random() * 20.0; // wait 15-35s before next spawn
        }
      }
    }
  }

  /**
   * Cleans up the Three.js rendering pipeline, disposes meshes, and unmounts elements.
   */
  destroy() {
    this.isDestroyed = true;

    // 1. Remove resize listener
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
    }

    // 2. Stop characters and controls
    if (this.player) {
      this.player.keys = { w: false, a: false, s: false, d: false };
      if (this.player.touchMoveVector) {
        this.player.touchMoveVector = { x: 0, z: 0 };
      }
    }

    // 3. Suspend synthesizers
    if (this.audio) {
      this.audio.stop();
    }

    // 4. Streamed sector cleanup
    if (this.generator) {
      for (const [key, sector] of this.generator.activeSectors.entries()) {
        this.generator.unloadSector(key, sector);
      }
    }

    // 5. Traverse and dispose geometries/materials
    this.scene.traverse((node) => {
      if (node.isMesh) {
        if (node.geometry) node.geometry.dispose();
        if (node.material) {
          if (Array.isArray(node.material)) {
            node.material.forEach((m) => m.dispose());
          } else {
            node.material.dispose();
          }
        }
      }
    });

    // 5.5. Clean up post-processing assets
    if (this.postScene) {
      this.postScene.traverse((node) => {
        if (node.isMesh) {
          if (node.geometry) node.geometry.dispose();
          if (node.material) node.material.dispose();
        }
      });
    }
    if (this.rt) {
      this.rt.dispose();
    }

    // 6. Clean renderer
    if (this.renderer) {
      this.renderer.dispose();
      const dom = this.renderer.domElement;
      if (dom && dom.parentNode) {
        dom.parentNode.removeChild(dom);
      }
    }

    console.log("WorldManager: WebGL rendering pipeline fully destroyed.");
  }
}
